import { ReportDateRange } from '../models/preview/preview.models';
import {
	hasReportMessage,
	reportMessage,
	reportMessageLanguage,
	sentenceStart
} from './report-messages';
import { REPORT_CUSTOM_PERIOD, utcSpanEnds } from './report-period';

/**
 * When a report has too few mentions to draw some of its sections honestly.
 *
 * A sentiment split, an emotion mix or a top-countries ranking read off a
 * couple of dozen mentions is noise that looks like a finding: three angry
 * posts become "34% negative". So under a floor those sections say how many
 * mentions there were and that they fill in later, instead of drawing a chart.
 *
 * SHARED CONTRACT (T-2384). The backend half applies the same floor to the
 * blocks it resolves — a thin block comes back `empty` with a note, "Only N
 * mentions in this period — too few to read <name> reliably. It fills in once
 * there are 50." — so a fixed section and an added chart say the same thing
 * about the same period. Change a value here and it changes there too, and the
 * other way round.
 *
 * THIN is fewer than the threshold mentions for the report's brand in the
 * report's window, counted with EXACT match: the total the executive summary's
 * Mentions tile shows, from the same read (ReportSharedReadsService).
 *
 * The five sections not listed below stay honest on any volume, so they draw
 * as usual: executive_summary, volume_of_mentions_reach,
 * mentions_by_media_type, top_influencers_by_voice_share and
 * top_sites_by_visits. A count is a count however small it is; a share or a
 * trend is not.
 */
export const REPORT_THIN_MENTION_THRESHOLD = 50;

/** The fixed sections that need volume to mean anything. */
export const REPORT_THIN_SENSITIVE_SECTION_IDS: ReadonlySet<string> = new Set([
	'sentiment_over_time',
	'emotion_over_time',
	'sentiment_by_media_type',
	'emotion_analysis',
	'influencers_by_media_type',
	'best_time_to_post',
	'website_traffic_demographics',
	'top_countries',
	'trending_hashtags',
	'trending_conversations',
	'languages'
]);

export function isThinSensitiveSection(sectionId: string): boolean {
	return REPORT_THIN_SENSITIVE_SECTION_IDS.has(sectionId);
}

/**
 * Whether a mention total is under the floor.
 *
 * A total that could not be read is NOT thin: blanking eleven sections of a
 * report because a request failed would be a worse lie than drawing them.
 */
export function isThinMentionTotal(total: number | null | undefined): boolean {
	return (
		typeof total === 'number' &&
		Number.isFinite(total) &&
		total < REPORT_THIN_MENTION_THRESHOLD
	);
}

/**
 * What a thin section says in place of its chart, in the report's language.
 *
 * `reads` is what the section would have shown, as a phrase ("sentiment over
 * time"). The period is said the way the reader picked it when the range
 * carries its key — a custom range by its dates — and as "this period" when
 * it does not.
 */
export function thinSectionMessage(
	total: number,
	reads: string,
	range?: ReportDateRange | null,
	language?: string | null
): string {
	const when = periodPhrase(range, language);
	const floor = REPORT_THIN_MENTION_THRESHOLD;
	if (total <= 0) {
		return reportMessage(language, 'thin.slide.none', {
			when,
			reads,
			floor
		});
	}
	const counted = reportMessage(language, 'thin.counted.some', {
		count: total
	});
	return reportMessage(language, 'thin.slide.some', {
		counted: sentenceStart(counted),
		when,
		reads,
		floor
	});
}

/**
 * "in the last 7 days", "between 1 Aug and 31 Aug 2026" for a custom range,
 * "so far" for an all-time report, else "in this period" — in `language`.
 */
function periodPhrase(
	range: ReportDateRange | null | undefined,
	language: string | null | undefined
): string {
	const period = range?.period;
	if (period === REPORT_CUSTOM_PERIOD && range) {
		return customWindowPhrase(range, language);
	}
	const key = `thin.window.${period}`;
	return reportMessage(
		language,
		period && hasReportMessage(key) ? key : 'thin.window.other'
	);
}

function customWindowPhrase(
	range: ReportDateRange,
	language: string | null | undefined
): string {
	const start = new Date(range.start);
	const end = new Date(range.end);
	const [from, to] =
		reportMessageLanguage(language) === 'en'
			? utcSpanEnds(start, end)
			: [numericDay(start), numericDay(end)];
	return from === to
		? reportMessage(language, 'thin.window.custom_day', { day: to })
		: reportMessage(language, 'thin.window.custom_range', {
				start: from,
				end: to
		  });
}

// CONTRACT: the backend's "%d.%m.%Y" for every language but English
// (`thin_data._custom_dates`), so a thin section and a thin chart name the
// same days alike.
function numericDay(date: Date): string {
	const day = String(date.getUTCDate()).padStart(2, '0');
	const month = String(date.getUTCMonth() + 1).padStart(2, '0');
	return `${day}.${month}.${date.getUTCFullYear()}`;
}
